import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/date";
import { MapPin, Ticket, CalendarClock, ChevronDown } from "lucide-react";
import { cheapestPrice, isCheapestLink } from "@/lib/prices";
import { accentFor } from "@/lib/accent";

const STOCK = [
  "https://images.unsplash.com/photo-1705482723134-f1703c6336af?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1562329265-95a6d7a83440?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1768381937064-0cff674a09ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1759502418465-62fb7c781e73?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1764405405911-67a3bdbf506d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1583343674297-d1bad99b5b47?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];
const stockFor = (id = "") => STOCK[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % STOCK.length];

function lowestPrice(ticketLinks) {
  if (!ticketLinks || ticketLinks.length === 0) return null;
  let lowest = null;
  for (const t of ticketLinks) {
    if (!t.starting_from) continue;
    const match = t.starting_from.match(/[\d,]+\.?\d*/);
    if (!match) continue;
    const value = parseFloat(match[0].replace(/,/g, ""));
    if (isNaN(value)) continue;
    if (lowest === null || value < lowest) lowest = value;
  }
  return lowest;
}

export function ShowCard({ show, index = 0 }) {
  const fallback = stockFor(show.id);
  const lowest = lowestPrice(show.ticket_links);
  const sellers = show.ticket_links || [];
  const hasMenu = sellers.length > 1;
  const cheapest = cheapestPrice(sellers);
  const primaryLink = sellers[0]?.url || show.ticket_link;
  const accent = accentFor(show.name);
  const isClosed = show.status === "Closed";

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      data-testid={`show-card-${show.id}`}
      className={`fade-up group relative flex flex-col rounded-2xl border border-white/10 bg-gradient-to-b from-[#211A27] to-[#17111C] shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-[#C89B3C]/50 hover:shadow-xl ${
        open ? "z-30" : ""
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <Link to={`/shows/${show.slug}`} className="relative block aspect-[4/3] overflow-hidden rounded-t-2xl bg-black/40">
        <img
          src={show.image_url || fallback}
          alt={show.name}
          className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = fallback; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute right-3 top-3">
          <StatusBadge status={show.status} testId={`status-${show.id}`} />
        </div>
        {show.is_sample && (
          <span className="absolute left-3 top-3 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black">
            Sample
          </span>
        )}
      </Link>

      <div className="h-[3px]" style={{ background: accent.bar }} />

      <div
        className="flex flex-1 flex-col rounded-b-2xl p-5"
        style={{ background: `linear-gradient(180deg, ${accent.tint}, transparent 60%)` }}
      >
        <Link to={`/shows/${show.slug}`}>
          <h3 className="font-serif text-xl font-semibold leading-tight text-amber-50 transition-colors group-hover:text-[#C89B3C]">
            {show.name}
          </h3>
        </Link>
        <div className="mt-1.5 flex items-center gap-1.5 text-sm text-amber-100/70">
          <MapPin className="h-3.5 w-3.5 text-[#C89B3C]" />
          {show.venue}
        </div>

        {show.booking_until && !isClosed && (
          <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-amber-50">
            <CalendarClock className="h-4 w-4 text-[#C89B3C]" />
            Booking until {fmtDate(show.booking_until)}
          </div>
        )}

        <div className="mt-auto pt-4">
        {lowest !== null && !isClosed && (
            <div className="mb-2 flex justify-end">
              <span className="rounded-full border border-[#C89B3C]/40 bg-[#C89B3C]/15 px-3 py-1 text-xs font-semibold text-[#C89B3C]">
                From £{lowest.toFixed(2)}
              </span>
            </div>
          )}

          {isClosed ? (
            <div
              data-testid={`closed-${show.id}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-amber-100/60"
            >
              Closed
            </div>
          ) : hasMenu ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`get-tickets-${show.id}`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#B82332]"
              >
                <Ticket className="h-4 w-4" /> Get Tickets
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>

              {open && (
                <div className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-72 overflow-y-auto rounded-2xl border border-amber-900/20 bg-[#161118] p-2 shadow-xl">
                  {sellers.map((t, i) => (
                    <a
                      key={i}
                      href={t.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-medium text-amber-50 transition-colors hover:bg-white/10 ${
                        isCheapestLink(t, cheapest) ? "bg-[#C89B3C]/10 ring-1 ring-[#C89B3C]/50" : ""
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {t.name}
                        {isCheapestLink(t, cheapest) && (
                          <span className="flex-shrink-0 rounded-full bg-[#C89B3C] px-2 py-0.5 text-[11px] font-bold text-black">cheapest</span>
                        )}
                      </span>
                      {t.starting_from && (
                        <span className="flex-shrink-0 text-[hsl(var(--gold))]">from {t.starting_from}</span>
                      )}
                    </a>
                  ))}
                  <p className="px-3 pt-2 text-xs text-amber-100/50">
                    Prices are indicative — check each site for current availability.
                  </p>
                </div>
              )}
            </div>
          ) : primaryLink ? (
            <a
              href={primaryLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`get-tickets-${show.id}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#B82332]"
            >
              <Ticket className="h-4 w-4" /> Get Tickets
            </a>
          ) : (
            <Link
              to={`/shows/${show.slug}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-medium text-amber-100/70 transition-colors hover:bg-white/10"
            >
              View details
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}